import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Filter, Grid, LayoutGrid, Image, Paintbrush, Pen, Camera, 
  Frame, X, ShoppingCart, Heart, Eye, ZoomIn, ChevronLeft, ChevronRight,
  Sparkles, Tag, Check, Loader2, Clock, Maximize2, Trash2, Plus, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import { ArtworkMagnifier, ArtworkDetailViewer } from '../components/ArtworkMagnifier';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';
import { useHero } from '@/hooks/useHero';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const API = process.env.REACT_APP_BACKEND_URL;

// Category icons mapping
const categoryIcons = {
  'pen-ink': Pen,
  'painting': Paintbrush,
  'print': Frame,
  'original': Sparkles,
  'photograph': Camera,
};

const categoryColors = {
  'pen-ink': 'amber',
  'painting': 'purple',
  'print': 'blue',
  'original': 'green',
  'photograph': 'pink',
};

// Artwork Card Component
const ArtworkCard = ({ artwork, onClick, onAddToCart, editEnabled = false, onPatch, onUpload, onDelete }) => {
  const [isHovered, setIsHovered] = useState(false);
  const Icon = categoryIcons[artwork.category] || Image;
  const color = categoryColors[artwork.category] || 'primary';
  const fileRef = useRef(null);

  const pickImage = (e) => {
    e.stopPropagation();
    fileRef.current && fileRef.current.click();
  };
  const handleFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f && onUpload) onUpload(artwork.id, f);
    e.target.value = '';
  };
  const patchField = (field) => (ev) => {
    const next = ev.currentTarget.textContent.trim();
    const cur = (artwork[field] ?? '').toString();
    if (next === cur || !onPatch) return;
    const value = field === 'price' ? parseFloat(next.replace(/[^0-9.]/g, '')) : next;
    if (field === 'price' && isNaN(value)) return;
    onPatch(artwork.id, { [field]: value });
  };
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ y: -5 }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative bg-card rounded-2xl overflow-hidden border border-border/50 hover:border-primary/30 transition-all cursor-pointer"
      onClick={() => !editEnabled && onClick(artwork)}
    >
      {editEnabled && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            style={{ display: 'none' }}
          />
          <button
            onClick={pickImage}
            className="absolute top-2 left-2 z-20 px-3 py-1.5 rounded-full bg-black/80 text-yellow-400 border border-yellow-400 text-xs font-bold flex items-center gap-1"
            data-testid={`art-upload-${artwork.id}`}
            title="Click to replace image"
          >
            <Upload className="w-3 h-3" /> Replace image
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete && onDelete(artwork); }}
            className="absolute top-2 right-2 z-20 w-8 h-8 rounded-full bg-red-600 text-white border-2 border-white flex items-center justify-center"
            data-testid={`art-delete-${artwork.id}`}
            title="Delete this artwork"
          >
            <X className="w-4 h-4" />
          </button>
        </>
      )}
      {/* Image Container */}
      <div className="relative aspect-[4/5] overflow-hidden bg-black">
        <img
          src={artwork.thumbnail_url || artwork.image_url}
          alt={artwork.title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        
        {/* Overlay on hover */}
        <div className={`absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute bottom-4 left-4 right-4 flex justify-between items-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClick(artwork);
              }}
              className="p-3 bg-white/20 backdrop-blur-sm rounded-full hover:bg-white/30 transition-colors"
            >
              <ZoomIn className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddToCart(artwork);
              }}
              className="px-4 py-2 bg-primary text-black rounded-full font-bold text-sm hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <ShoppingCart className="w-4 h-4" />
              Add to Cart
            </button>
          </div>
        </div>
        
        {/* Badges */}
        <div className="absolute top-3 left-3 flex flex-col gap-2">
          {artwork.is_original && (
            <span className="px-2 py-1 bg-green-500 text-white text-xs font-bold rounded-full flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Original
            </span>
          )}
          {artwork.is_featured && (
            <span className="px-2 py-1 bg-amber-500 text-black text-xs font-bold rounded-full">
              Featured
            </span>
          )}
          {artwork.original_price && artwork.original_price > artwork.price && (
            <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
              Sale
            </span>
          )}
        </div>
        
        {/* Category Badge */}
        <div className="absolute top-3 right-3">
          <span className={`p-2 bg-${color}-500/80 backdrop-blur-sm rounded-full`}>
            <Icon className="w-4 h-4 text-white" />
          </span>
        </div>
        
        {/* Sold badge */}
        {artwork.is_sold && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="px-6 py-3 bg-red-600 text-white font-bold text-lg rounded-full transform -rotate-12">
              SOLD
            </span>
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="p-4">
        <h3
          className="font-bold text-foreground line-clamp-1 mb-1 group-hover:text-primary transition-colors"
          contentEditable={editEnabled}
          suppressContentEditableWarning
          onClick={editEnabled ? (e) => e.stopPropagation() : undefined}
          onBlur={editEnabled ? patchField('title') : undefined}
          onKeyDown={(e) => { if (editEnabled) { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } } }}
          style={editEnabled ? { outline: '2px dashed rgba(244,180,0,0.6)', outlineOffset: 2, borderRadius: 3 } : undefined}
          data-testid={`art-title-${artwork.id}`}
        >
          {artwork.title}
        </h3>
        <p
          className="text-sm text-muted-foreground line-clamp-2 mb-3"
          contentEditable={editEnabled}
          suppressContentEditableWarning
          onClick={editEnabled ? (e) => e.stopPropagation() : undefined}
          onBlur={editEnabled ? patchField(artwork.description ? 'description' : 'medium') : undefined}
          onKeyDown={(e) => { if (editEnabled) { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } } }}
          style={editEnabled ? { outline: '2px dashed rgba(244,180,0,0.6)', outlineOffset: 2, borderRadius: 3 } : undefined}
          data-testid={`art-desc-${artwork.id}`}
        >
          {artwork.medium || artwork.description}
        </p>
        
        {/* Pricing - Clear distinction between Original and Prints */}
        <div className="space-y-1">
          {/* Original Price */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-green-400 bg-green-500/20 px-2 py-0.5 rounded">
                ORIGINAL
              </span>
              <span
                className="text-lg font-bold text-primary"
                contentEditable={editEnabled}
                suppressContentEditableWarning
                onClick={editEnabled ? (e) => e.stopPropagation() : undefined}
                onBlur={editEnabled ? patchField('price') : undefined}
                onKeyDown={(e) => { if (editEnabled) { e.stopPropagation(); if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } } }}
                style={editEnabled ? { outline: '2px dashed rgba(244,180,0,0.6)', outlineOffset: 2, borderRadius: 3, minWidth: 60, display: 'inline-block' } : undefined}
                data-testid={`art-price-${artwork.id}`}
              >
                ${artwork.price.toFixed(2)}
              </span>
            </div>
            {artwork.is_sold && (
              <span className="text-xs text-red-400 font-medium">Sold</span>
            )}
          </div>
          
          {/* Prints Starting Price */}
          {artwork.prints_available && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-blue-400 bg-blue-500/20 px-2 py-0.5 rounded">
                PRINTS
              </span>
              <span className="text-sm text-muted-foreground">
                from <span className="text-blue-400 font-semibold">${(artwork.price * 0.15).toFixed(2)}</span>
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Lightbox Modal Component with Print Options
const ArtworkModal = ({ artwork, onClose, onAddToCart }) => {
  const [printOptions, setPrintOptions] = useState(null);
  const [selectedPurchaseType, setSelectedPurchaseType] = useState('original'); // 'original' or 'print'
  const [selectedPrintSize, setSelectedPrintSize] = useState('medium');
  const [loadingPrints, setLoadingPrints] = useState(false);
  const [showDetailViewer, setShowDetailViewer] = useState(false);
  
  useEffect(() => {
    if (artwork?.prints_available || artwork?.id) {
      fetchPrintOptions();
    }
  }, [artwork]);
  
  const fetchPrintOptions = async () => {
    if (!artwork?.id) return;
    setLoadingPrints(true);
    try {
      const res = await fetch(`${API}/api/artwork-prints/artwork/${artwork.id}/options`);
      if (res.ok) {
        const data = await res.json();
        setPrintOptions(data);
        // Default to print if original is sold
        if (data.prints_available && !data.original_available) {
          setSelectedPurchaseType('print');
        }
      }
    } catch (err) {
      console.error('Failed to load print options:', err);
    } finally {
      setLoadingPrints(false);
    }
  };
  
  const getCurrentPrice = () => {
    if (selectedPurchaseType === 'original') {
      return artwork.price;
    }
    return printOptions?.print_options?.[selectedPrintSize]?.price || 0;
  };
  
  const handleAddToCart = () => {
    const item = {
      ...artwork,
      purchase_type: selectedPurchaseType,
      selected_size: selectedPurchaseType === 'print' ? selectedPrintSize : null,
      final_price: getCurrentPrice(),
      size_name: selectedPurchaseType === 'print' ? printOptions?.print_options?.[selectedPrintSize]?.size_name : 'Original'
    };
    onAddToCart(item);
  };

  if (!artwork) return null;
  
  return (
    <>
      {/* Full Screen Detail Viewer */}
      <ArtworkDetailViewer
        src={artwork.image_url}
        alt={artwork.title}
        isOpen={showDetailViewer}
        onClose={() => setShowDetailViewer(false)}
        title={artwork.title}
      />
      
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-card rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col md:flex-row"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Image with Magnifier */}
          <div className="md:w-2/3 bg-black relative">
            <ArtworkMagnifier
              src={artwork.image_url}
              alt={artwork.title}
              zoomLevel={3}
              magnifierSize={180}
              className="w-full h-full max-h-[50vh] md:max-h-[80vh]"
            />
            
            {/* Full Screen View Button */}
            <button
              onClick={() => setShowDetailViewer(true)}
              className="absolute top-4 left-4 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors flex items-center gap-2 text-white text-sm"
              data-testid="fullscreen-view-btn"
            >
              <Maximize2 className="w-5 h-5" />
              <span className="hidden sm:inline">Full View</span>
            </button>
            
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full transition-colors"
              data-testid="close-modal-btn"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            
            {/* Pen & Ink Detail Hint */}
            {artwork.category === 'pen-ink' && (
              <div className="absolute bottom-4 left-4 bg-amber-500/90 text-black px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-2">
                <ZoomIn className="w-4 h-4" />
                Hover to magnify intricate details
              </div>
            )}
          </div>
        
        {/* Details */}
        <div className="md:w-1/3 p-6 overflow-y-auto">
          <div className="space-y-4">
            {artwork.is_original && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-400 text-sm font-bold rounded-full">
                <Sparkles className="w-4 h-4" /> Original Artwork
              </span>
            )}
            
            <h2 className="text-2xl font-bold text-foreground">{artwork.title}</h2>
            
            {/* Price Summary Box */}
            <div className="bg-gradient-to-br from-zinc-800/80 to-zinc-900/80 rounded-xl p-4 border border-zinc-700/50">
              <div className="space-y-3">
                {/* Original Price */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded">ORIGINAL</span>
                    <span className="text-sm text-muted-foreground">One-of-a-kind piece</span>
                  </div>
                  <span className="text-xl font-bold text-primary">${artwork.price?.toFixed(2)}</span>
                </div>
                
                {/* Prints Price Range */}
                {artwork.prints_available !== false && (
                  <div className="flex items-center justify-between pt-2 border-t border-zinc-700/50">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs font-bold rounded">PRINTS</span>
                      <span className="text-sm text-muted-foreground">Digital download</span>
                    </div>
                    <span className="text-lg font-semibold text-blue-400">
                      ${(artwork.price * 0.15).toFixed(2)} - ${(artwork.price * 0.40).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            <p className="text-muted-foreground">{artwork.description}</p>
            
            <div className="space-y-2 text-sm">
              {artwork.medium && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Medium</span>
                  <span className="text-foreground font-medium">{artwork.medium}</span>
                </div>
              )}
              {artwork.dimensions && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Dimensions</span>
                  <span className="text-foreground font-medium">{artwork.dimensions}</span>
                </div>
              )}
              {artwork.year_created && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Year</span>
                  <span className="text-foreground font-medium">{artwork.year_created}</span>
                </div>
              )}
            </div>
            
            {/* Purchase Type Selection */}
            {printOptions?.prints_available && (
              <div className="border-t border-border/50 pt-4">
                <p className="text-sm font-medium text-foreground mb-3">Choose Purchase Type:</p>
                <div className="space-y-2">
                  {/* Original Option */}
                  {printOptions.original_available && (
                    <label 
                      className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedPurchaseType === 'original' 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="purchaseType"
                          checked={selectedPurchaseType === 'original'}
                          onChange={() => setSelectedPurchaseType('original')}
                          className="w-4 h-4 accent-primary"
                        />
                        <div>
                          <span className="font-medium text-foreground">Original Artwork</span>
                          <p className="text-xs text-muted-foreground">One-of-a-kind piece</p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-primary">${artwork.price?.toFixed(2)}</span>
                    </label>
                  )}
                  
                  {/* Print Option */}
                  <label 
                    className={`flex items-center justify-between p-3 rounded-xl border-2 transition-all ${
                      selectedPurchaseType === 'print' 
                        ? 'border-green-500 bg-green-500/10' 
                        : 'border-border hover:border-green-500/50'
                    } cursor-pointer`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="purchaseType"
                        checked={selectedPurchaseType === 'print'}
                        onChange={() => setSelectedPurchaseType('print')}
                        className="w-4 h-4 accent-green-500"
                      />
                      <div>
                        <span className="font-medium text-foreground">Digital Print</span>
                        <p className="text-xs text-green-400">High-res download</p>
                      </div>
                    </div>
                    <span className="text-sm text-muted-foreground">Select size below</span>
                  </label>
                </div>
              </div>
            )}
            
            {/* Print Size Selection */}
            {selectedPurchaseType === 'print' && printOptions?.print_options && (
              <div className="bg-zinc-800/50 rounded-xl p-4 space-y-2">
                <p className="text-sm font-medium text-foreground mb-2">Select Print Size:</p>
                {Object.entries(printOptions.print_options).map(([key, option]) => (
                  <label 
                    key={key}
                    className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                      selectedPrintSize === key 
                        ? 'bg-green-500/20 border border-green-500' 
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="printSize"
                        checked={selectedPrintSize === key}
                        onChange={() => setSelectedPrintSize(key)}
                        className="w-3 h-3 accent-green-500"
                      />
                      <div>
                        <span className="text-sm font-medium text-foreground">{option.size_name}</span>
                        <span className="text-xs text-muted-foreground ml-2">({option.dimensions})</span>
                      </div>
                    </div>
                    <span className="text-green-400 font-bold">${option.price?.toFixed(2)}</span>
                  </label>
                ))}
                <p className="text-xs text-muted-foreground mt-2">
                  Instant download after purchase (3 downloads, 2-day expiry)
                </p>
              </div>
            )}
            
            <div className="border-t border-border/50 pt-4">
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-3xl font-bold text-primary">${getCurrentPrice().toFixed(2)}</span>
                {selectedPurchaseType === 'original' && artwork.original_price && artwork.original_price > artwork.price && (
                  <span className="text-lg text-muted-foreground line-through">
                    ${artwork.original_price.toFixed(2)}
                  </span>
                )}
                {selectedPurchaseType === 'print' && (
                  <span className="text-sm text-green-400">Digital Print</span>
                )}
              </div>
              
              {(!artwork.is_sold || selectedPurchaseType === 'print') ? (
                <button
                  onClick={() => {
                    handleAddToCart();
                    onClose();
                  }}
                  className="w-full py-4 bg-primary hover:bg-primary/90 text-black rounded-xl font-bold text-lg flex items-center justify-center gap-2 transition-colors"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {selectedPurchaseType === 'print' ? 'Buy Print' : 'Add to Cart'}
                </button>
              ) : (
                <div className="w-full py-4 bg-gray-600 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-2">
                  Original Sold
                </div>
              )}
              
              {artwork.stock > 1 && selectedPurchaseType === 'original' && !artwork.is_original && (
                <p className="text-center text-sm text-muted-foreground mt-2">
                  {artwork.stock} prints available
                </p>
              )}
            </div>
            
            {artwork.tags && artwork.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-4">
                {artwork.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-white/5 text-muted-foreground text-xs rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
    </>
  );
};

// Main Artwork Store Page
export const ArtworkStore = () => (
  <PageEditProvider slug="artwork">
    <ArtworkStoreInner />
    <PageEditToolbar />
    <AdminEditorLink to="/admin/dashboard#artwork-editor-btn" label="Edit Artwork" />
  </PageEditProvider>
);

const ArtworkStoreInner = () => {
  const hero = useHero('artwork');
  const { enabled: editEnabled } = usePageEditMode();
  const [artwork, setArtwork] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedArtwork, setSelectedArtwork] = useState(null);
  const [showOriginalOnly, setShowOriginalOnly] = useState(false);
  const [sortBy, setSortBy] = useState('featured');
  const [viewMode, setViewMode] = useState('grid'); // grid or masonry

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [artworkRes, categoriesRes] = await Promise.all([
        fetch(`${API}/api/artwork`),
        fetch(`${API}/api/artwork/categories`)
      ]);
      
      const artworkData = await artworkRes.json();
      const categoriesData = await categoriesRes.json();
      
      setArtwork(artworkData);
      setCategories(categoriesData);
    } catch (err) {
      console.error('Failed to fetch artwork:', err);
      toast.error('Failed to load artwork');
    } finally {
      setLoading(false);
    }
  };

  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  });

  const patchArtwork = async (id, patch) => {
    try {
      const res = await fetch(`${API}/api/artwork/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Save failed');
      toast.success('Saved');
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };

  const uploadArtworkImage = async (id, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const up = await fetch(`${API}/api/artwork-prints/upload`, {
        method: 'POST', headers: authHeaders(), body: fd,
      });
      if (!up.ok) throw new Error('Upload failed');
      const data = await up.json();
      const newUrl = data.original_url || data.url;
      if (!newUrl) throw new Error('No URL returned by upload');
      await patchArtwork(id, { image_url: newUrl });
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    }
  };

  const deleteArtwork = async (art) => {
    if (!window.confirm(`Delete "${art.title}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/api/artwork/${art.id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Delete failed');
      toast.success('Artwork deleted');
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    }
  };

  const addArtwork = async () => {
    const title = window.prompt('Title for the new artwork:');
    if (!title || !title.trim()) return;
    try {
      const res = await fetch(`${API}/api/artwork`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          title: title.trim(),
          description: '',
          category: 'pen-ink',
          price: 100,
          image_url: 'https://via.placeholder.com/800x1000.png?text=Click+Replace+Image',
          is_original: true,
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Create failed');
      toast.success('Added — click Replace image on the new card to upload');
      fetchData();
    } catch (e) {
      toast.error(e.message || 'Create failed');
    }
  };

  // Filter and sort artwork
  const filteredArtwork = artwork
    .filter(item => {
      if (selectedCategory !== 'all' && item.category !== selectedCategory) return false;
      if (showOriginalOnly && !item.is_original) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          item.title?.toLowerCase().includes(query) ||
          item.description?.toLowerCase().includes(query) ||
          item.tags?.some(tag => tag.toLowerCase().includes(query))
        );
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-low': return a.price - b.price;
        case 'price-high': return b.price - a.price;
        case 'newest': return new Date(b.created_at) - new Date(a.created_at);
        case 'featured':
        default:
          if (a.is_featured && !b.is_featured) return -1;
          if (!a.is_featured && b.is_featured) return 1;
          return a.order - b.order;
      }
    });

  const handleAddToCart = async (item) => {
    // Get existing cart from localStorage
    const existingCart = JSON.parse(localStorage.getItem('cart') || '[]');
    
    // Check if item already in cart
    const existingIndex = existingCart.findIndex(
      cartItem => cartItem.id === item.id && cartItem.type === 'artwork'
    );
    
    if (existingIndex >= 0) {
      toast.info('This item is already in your cart');
      return;
    }
    
    // Add to cart
    existingCart.push({
      id: item.id,
      type: 'artwork',
      title: item.title,
      price: item.price,
      image: item.thumbnail_url || item.image_url,
      quantity: 1
    });
    
    localStorage.setItem('cart', JSON.stringify(existingCart));
    
    // Dispatch event for cart update
    window.dispatchEvent(new Event('cartUpdated'));
    
    toast.success(`Added "${item.title}" to cart`);
  };

  const totalArtwork = artwork.filter(a => !a.is_sold).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Paintbrush className="w-12 h-12 text-primary" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-background to-amber-900/20" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 right-10 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-amber-500/30 rounded-full blur-3xl" />
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative max-w-7xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/20 border border-purple-500/30 mb-6">
            <Paintbrush className="w-5 h-5 text-purple-400" />
            <span className="text-sm font-bold text-purple-400 uppercase tracking-wider">
              <PageEditable path="hero.badge" as="span" fallback="Original Artwork by Mestre Curtis Pierre" />
            </span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-display text-foreground mb-4">
            <PageEditable path="hero.title" as="span" fallback={hero.title_top || "Art Gallery"} />
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            <PageEditable path="hero.subtitle" as="span" multiline fallback="Discover unique pen & ink drawings, paintings, prints, and photographs that capture the spirit of Afro-Brasilian culture and music." />
          </p>

          {/* Search Bar */}
          <div className="max-w-xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search artwork, styles, themes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-card border border-border/50 rounded-2xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-full"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-6">
        {editEnabled && (
          <div className="mb-6 flex items-center gap-3 flex-wrap p-4 rounded-xl border-2 border-dashed border-yellow-500/60 bg-yellow-500/10">
            <span className="text-sm text-yellow-200 flex-1">
              <strong>Edit mode on.</strong> Click any title, description or price to retype it. Click <em>Replace image</em> on a card to upload a new photo. Click <em>×</em> to delete. Save with the bottom-left button when done.
            </span>
            <button
              onClick={addArtwork}
              data-testid="art-add-btn"
              className="px-4 py-2 rounded-full bg-yellow-400 text-black font-bold text-sm flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Add artwork
            </button>
          </div>
        )}
        {/* Category Filter Pills */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full font-medium transition-all ${
              selectedCategory === 'all'
                ? 'bg-primary text-black'
                : 'bg-card border border-border/50 text-foreground hover:border-primary/30'
            }`}
          >
            All ({totalArtwork})
          </button>
          
          {categories.map((cat) => {
            const Icon = categoryIcons[cat.id] || Image;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === cat.id
                    ? 'bg-primary text-black'
                    : 'bg-card border border-border/50 text-foreground hover:border-primary/30'
                }`}
              >
                <Icon className="w-4 h-4" />
                {cat.name} ({cat.count})
              </button>
            );
          })}
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-6 border-b border-border/30">
          <div className="flex items-center gap-4">
            {/* Originals Only Toggle */}
            <button
              onClick={() => setShowOriginalOnly(!showOriginalOnly)}
              className={`px-4 py-2 rounded-lg font-medium transition-all flex items-center gap-2 ${
                showOriginalOnly
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                  : 'bg-card border border-border/50 text-muted-foreground hover:text-foreground'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              Originals Only
              {showOriginalOnly && <Check className="w-4 h-4" />}
            </button>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 bg-card border border-border/50 rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="featured">Featured</option>
              <option value="newest">Newest</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
            
            {/* View Mode Toggle */}
            <div className="flex bg-card border border-border/50 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-black' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Grid className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewMode('masonry')}
                className={`p-2 ${viewMode === 'masonry' ? 'bg-primary text-black' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <LayoutGrid className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <p className="text-sm text-muted-foreground mb-6">
          Showing {filteredArtwork.length} artwork{filteredArtwork.length !== 1 ? 's' : ''}
          {searchQuery && ` for "${searchQuery}"`}
        </p>

        {/* Artwork Grid */}
        {filteredArtwork.length === 0 ? (
          <div className="text-center py-20">
            <Paintbrush className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
            {searchQuery ? (
              <>
                <p className="text-lg text-foreground mb-2">No artwork found for "{searchQuery}"</p>
                <button
                  onClick={() => setSearchQuery('')}
                  className="text-primary hover:underline"
                >
                  Clear search
                </button>
              </>
            ) : (
              <p className="text-lg text-muted-foreground">No artwork available in this category yet.</p>
            )}
          </div>
        ) : (
          <motion.div 
            layout
            className={`grid gap-6 ${
              viewMode === 'masonry'
                ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            }`}
          >
            <AnimatePresence>
              {filteredArtwork.map((item) => (
                <ArtworkCard
                  key={item.id}
                  artwork={item}
                  onClick={setSelectedArtwork}
                  onAddToCart={handleAddToCart}
                  editEnabled={editEnabled}
                  onPatch={patchArtwork}
                  onUpload={uploadArtworkImage}
                  onDelete={deleteArtwork}
                />
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedArtwork && (
          <ArtworkModal
            artwork={selectedArtwork}
            onClose={() => setSelectedArtwork(null)}
            onAddToCart={handleAddToCart}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ArtworkStore;
